// Mirrors server cyborg/provider-error-classify.ts; the source test fails when the tables differ.
import type { ProviderReasonKind } from "./types.js";

// Same as the server's: "expired" only beside a credential word, never a timeout or a TLS cert.
export const CREDENTIAL_EXPIRED =
  /(?<![a-z0-9])(?:[a-z0-9]*token|api[ _-]?key|key|jwt|credential|session|log[- ]?in|sign[- ]?in|auth|authentication|authori[sz]ation|oauth|password)s?(?:(?![a-z0-9])(?:[^\n.]|\.(?=\S)){0,30}(?<!un))?expired|(?<![a-z0-9])expired[ _-]?(?:(?:oauth|access|refresh|bearer|id|security|sso|api)[ _-]?)?(?:token|key|jwt|credential|session|log[- ]?in|sign[- ]?in|auth|password)/;

export const STORED_FAILURE_RULES: ReadonlyArray<
  readonly [ProviderReasonKind, readonly (string | RegExp)[]]
> = [
  ["model_unavailable", ["needs a newer claude code"]],
  ["model_unavailable", ["modeldeprecated", "model_deprecated"]],
  [
    "model_unavailable",
    [
      "model not found",
      "unknown model",
      "invalid model",
      "unsupported model",
      "model_not_found",
      "does not support model",
      "no such model",
      "issue with the selected model",
      "does not exist or you do not have access",
      "does not exist or your team",
      "not a valid model",
      "is not found for api version",
    ],
  ],
  [
    "usage_gated",
    [
      "insufficient_quota",
      "no credits remaining",
      "exceeded your current quota",
      "billing_hard_limit",
      "credit balance is too low",
      "insufficient credits",
      "insufficient balance",
      "used all available credits",
      "monthly spending limit",
      "requires more credits",
      "http 402",
      "payment required",
    ],
  ],
  ["usage_gated", ["[codex_error_info: usagelimitexceeded"]],
  ["usage_gated", ["extra usage", "settings/usage"]],
  [
    "usage_gated",
    [
      "usage limit",
      "usagelimitexceeded",
      "hit your limit",
      "session limit",
      "weekly limit",
      "opus limit",
      "sonnet limit",
      "rate_limit: rejected",
    ],
  ],
  ["rate_limited", ["rate limit", "rate_limit", "http 429", "too many requests", "overloaded"]],
  [
    "expired",
    [
      CREDENTIAL_EXPIRED,
      "reauthenticate",
      "re-authenticate",
      "oauth refresh failed",
      "invalid_grant",
      "aws sso login",
    ],
  ],
  ["not_configured", ["no api key"]],
  [
    "auth_invalid",
    [
      "http 401",
      "unauthorized",
      "api key",
      "api-key",
      "invalid_api_key",
      "authentication_error",
      "permission_error",
      "failed to authenticate",
    ],
  ],
  ["not_configured", ["no auth", "not configured", "no credentials", "not logged in"]],
];

// Same as the server's: a status code only where the text says it is one, never a bare digit run.
export const HTTP_STATUS =
  /(?:^|\b(?:status(?:[_ ]?code)?|http(?:\/[\d.]+)?|code|error)["']?\s*[:=]?\s*)([1-5]\d\d)(?![\w.])/gim;

export const INTERNAL_CRASH_PHRASES: readonly string[] = [
  "is not a function",
  "cannot read properties of",
  "cannot read property",
  "is not defined",
  "undefined is not an object",
];

// The daemon words these itself (a missing folder, Codex's unsupported model): the row stays plain.
const DAEMON_WORDED = [
  /working directory does not exist:/i,
  /path\s+"[^"]*\.cybo[/\\][^"]*"\s+does not exist/i,
  /native binary[^\n]*failed to launch/i,
  /model is not supported when using codex/i,
];
const NO_PATH_MATCHES_ROW = /^\s*PATH matches:\s*none\s*$/im;
const UNRESOLVED_BINARY_ROW = /^\s*Resolved path:\s*not found\s*$/im;

function kindOf(text: string): ProviderReasonKind {
  const lower = text.trim().toLowerCase();
  if (lower === "(empty)") return "not_configured";
  const haystack = [lower, ...[...text.matchAll(HTTP_STATUS)].map((m) => `http ${m[1]}`)].join(
    "\n",
  );
  const rule = STORED_FAILURE_RULES.find(([, needles]) =>
    needles.some((n) => (typeof n === "string" ? haystack.includes(n) : n.test(haystack))),
  );
  if (rule) return rule[0];
  if (INTERNAL_CRASH_PHRASES.some((phrase) => lower.includes(phrase))) return "internal_error";
  const missingBinary = NO_PATH_MATCHES_ROW.test(text) && UNRESOLVED_BINARY_ROW.test(text);
  return missingBinary ? "not_installed" : "unknown";
}

/** The reason kind the daemon gave the live failure, read back from the stored row's text. */
export function storedFailureKind(error: string, diagnostic = ""): ProviderReasonKind | undefined {
  if (DAEMON_WORDED.some((re) => re.test(error))) return undefined;
  const kind = kindOf(error);
  const settled = kind === "unknown" && diagnostic ? kindOf(diagnostic) : kind;
  return settled === "unknown" ? undefined : settled;
}
